import { z } from 'zod';
import { EXAM_CODE_MAX_LENGTH, EXAM_CODE_MIN_LENGTH, normalizeExamCode } from './exam-code.js';
import { ADMIN_ROLES, ATTEMPT_STATUSES, COMPLETION_STATUSES, ERROR_CODES, EXAM_KINDS, EXAM_STATUSES, INVITE_IMPORT_MAX_ROWS, MAX_COMPARE_COURSES, QUESTION_TYPES, } from './constants.js';
/** schema เดียว ใช้ทั้ง runtime validation และ type inference */
export const uuidSchema = z.string().uuid();
export const isoDateTimeSchema = z.string().datetime({ offset: true });
export const questionTypeSchema = z.enum(QUESTION_TYPES);
export const examKindSchema = z.enum(EXAM_KINDS);
export const examStatusSchema = z.enum(EXAM_STATUSES);
export const attemptStatusSchema = z.enum(ATTEMPT_STATUSES);
export const adminRoleSchema = z.enum(ADMIN_ROLES);
export const completionStatusSchema = z.enum(COMPLETION_STATUSES);
export const errorCodeSchema = z.nativeEnum(ERROR_CODES);
/** อีเมลคือกุญแจจับคู่ pre/post — normalize ให้เหมือนกันทุกจุดที่รับเข้ามา */
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(254)
    .email('รูปแบบอีเมลไม่ถูกต้อง');
export const fullNameSchema = z
    .string()
    .trim()
    .min(2, 'กรุณากรอกชื่อ-นามสกุลอย่างน้อย 2 ตัวอักษร')
    .max(200);
// ─── Public: ผู้เรียน ──────────────────────────────────────────────────────
export const examTokenParamSchema = z.object({
    token: z.string().min(8).max(64),
});
/**
 * รหัสสั้นที่ผู้เรียนพิมพ์เอง — normalize ก่อนเสมอ (พิมพ์เล็ก/ใส่ขีด/เว้นวรรคก็ต้องเจอ)
 * ความยาวเช็คหลัง normalize เพราะ "mal-2569-a1" ยาว 11 แต่รหัสจริงคือ 9 ตัว
 */
export const examCodeSchema = z
    .string()
    .trim()
    .max(64)
    .transform(normalizeExamCode)
    .refine((value) => value.length >= EXAM_CODE_MIN_LENGTH && value.length <= EXAM_CODE_MAX_LENGTH, `รหัสแบบทดสอบต้องมี ${EXAM_CODE_MIN_LENGTH}-${EXAM_CODE_MAX_LENGTH} ตัวอักษร (ตัวอักษรและตัวเลขเท่านั้น)`);
export const examCodeParamSchema = z.object({ code: examCodeSchema });
/** ผลการค้นหาด้วยรหัสสั้น — คืนแค่ทางเข้า ไม่ใช่ข้อมูลข้อสอบ */
export const examCodeLookupSchema = z.object({
    accessToken: z.string(),
});
export const startExamBodySchema = z.object({
    fullName: fullNameSchema,
    email: emailSchema,
    organization: z.string().trim().max(200).optional(),
    phone: z.string().trim().max(30).optional(),
    consent: z.literal(true, {
        errorMap: () => ({ message: 'ต้องยินยอมให้เก็บข้อมูลก่อนจึงจะเริ่มทำแบบทดสอบได้' }),
    }),
    /** honeypot กันบอท — ต้องว่างเสมอ ดู docs/07-security.md */
    website: z.string().max(200).optional(),
});
// ─── Public: ลิงก์ส่วนตัวจากรายชื่อที่นำเข้าล่วงหน้า (Phase 6) ─────────────
export const inviteTokenParamSchema = z.object({
    token: z.string().min(8).max(64),
});
/** สถานะของผู้ถือลิงก์ต่อชุดข้อสอบหนึ่งชุด */
export const inviteExamSetStatusSchema = z.enum([
    'not_started',
    'in_progress',
    'submitted',
    'expired',
]);
export const inviteExamSetSchema = z.object({
    examSetId: uuidSchema,
    kind: examKindSchema,
    title: z.string(),
    instructions: z.string().nullable(),
    questionCount: z.number().int(),
    durationMinutes: z.number().int().nullable(),
    /** เปิดให้ทำอยู่ตอนนี้หรือไม่ (ดูทั้ง status และช่วงเวลา) */
    isOpen: z.boolean(),
    status: inviteExamSetStatusSchema,
    /** กดเริ่ม/ทำต่อได้หรือไม่ — false เมื่อปิดอยู่ หรือทำครบจำนวนครั้งแล้ว */
    canStart: z.boolean(),
});
/**
 * หน้าส่วนตัวของผู้ถือลิงก์ — ลิงก์ผูกกับหลักสูตร ไม่ใช่ชุดข้อสอบ
 * จึงลิสต์ชุดที่มีในหลักสูตรให้เลือก พร้อมบอกว่าทำอันไหนไปแล้ว
 * ห้ามมีข้อมูลข้อสอบหรือเฉลยติดมา — ดู docs/07-security.md
 */
export const inviteLandingSchema = z.object({
    courseTitle: z.string(),
    fullName: z.string(),
    email: z.string(),
    organization: z.string().nullable(),
    phone: z.string().nullable(),
    serverTime: isoDateTimeSchema,
    examSets: z.array(inviteExamSetSchema),
});
/**
 * เริ่มทำข้อสอบจากลิงก์ส่วนตัว — ชื่อ/อีเมลมาจากรายชื่อที่นำเข้าไว้ ไม่รับจาก client
 * แต่ยังต้องติ๊กยินยอม PDPA เองทุกครั้ง ติ๊กไว้ล่วงหน้าแทนเจ้าตัวไม่ได้
 */
export const startFromInviteBodySchema = z.object({
    examSetId: uuidSchema,
    consent: z.literal(true, {
        errorMap: () => ({ message: 'ต้องยินยอมให้เก็บข้อมูลก่อนจึงจะเริ่มทำแบบทดสอบได้' }),
    }),
    /** honeypot กันบอท — ต้องว่างเสมอ ดู docs/07-security.md */
    website: z.string().max(200).optional(),
});
export const submittedAnswerSchema = z.object({
    questionId: uuidSchema,
    selectedOptionIds: z.array(uuidSchema).max(20).optional(),
    textAnswer: z.string().max(2000).nullish(),
    isFlagged: z.boolean().optional(),
});
export const saveAnswersBodySchema = z.object({
    answers: z.array(submittedAnswerSchema).max(500),
});
export const submitAttemptBodySchema = z.object({
    answers: z.array(submittedAnswerSchema).max(500).default([]),
});
export const publicOptionSchema = z.object({
    id: uuidSchema,
    label: z.string(),
});
/** ห้ามมี isCorrect / explanation / pairKey / topic เด็ดขาด — ดู docs/07-security.md */
export const publicQuestionSchema = z.object({
    id: uuidSchema,
    type: questionTypeSchema,
    stem: z.string(),
    imageUrl: z.string().nullable(),
    points: z.number(),
    options: z.array(publicOptionSchema),
});
export const examInfoSchema = z.object({
    examSetId: uuidSchema,
    kind: examKindSchema,
    title: z.string(),
    instructions: z.string().nullable(),
    courseTitle: z.string(),
    questionCount: z.number().int(),
    durationMinutes: z.number().int().nullable(),
    questionsPerPage: z.number().int(),
    isOpen: z.boolean(),
    serverTime: isoDateTimeSchema,
});
export const savedAnswerSchema = z.object({
    questionId: uuidSchema,
    selectedOptionIds: z.array(uuidSchema),
    textAnswer: z.string().nullable(),
    isFlagged: z.boolean(),
});
export const attemptStateSchema = z.object({
    sessionToken: z.string().optional(),
    attemptId: uuidSchema,
    questionsPerPage: z.number().int().min(1),
    startedAt: isoDateTimeSchema,
    deadlineAt: isoDateTimeSchema.nullable(),
    serverTime: isoDateTimeSchema,
    remainingSeconds: z.number().nullable(),
    questions: z.array(publicQuestionSchema),
    savedAnswers: z.array(savedAnswerSchema),
});
export const saveAnswersResultSchema = z.object({
    savedAt: isoDateTimeSchema,
    serverTime: isoDateTimeSchema,
    remainingSeconds: z.number().nullable(),
});
export const answerKeySchema = z.object({
    questionId: uuidSchema,
    isCorrect: z.boolean(),
    correctOptionIds: z.array(uuidSchema),
    explanation: z.string().nullable(),
});
export const attemptResultSchema = z.object({
    rawScore: z.number(),
    maxScore: z.number(),
    percentScore: z.number(),
    isPassed: z.boolean().nullable(),
    questions: z.array(answerKeySchema).optional(),
});
export const submitResultSchema = z.object({
    submittedAt: isoDateTimeSchema,
    durationSeconds: z.number().int(),
    status: attemptStatusSchema,
    showResult: z.boolean(),
    result: attemptResultSchema.optional(),
});
// ─── Admin ────────────────────────────────────────────────────────────────
export const loginBodySchema = z.object({
    email: emailSchema,
    password: z.string().min(8).max(200),
});
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(20),
});
export const courseInputSchema = z.object({
    code: z.string().trim().min(2).max(50),
    titleTh: z.string().trim().min(2).max(300),
    titleEn: z.string().trim().max(300).nullish(),
    description: z.string().max(5000).nullish(),
    startDate: z.string().date().nullish(),
    endDate: z.string().date().nullish(),
});
export const examSetInputSchema = z.object({
    kind: examKindSchema,
    title: z.string().trim().min(2).max(300),
    instructions: z.string().max(10_000).nullish(),
    durationMinutes: z.number().int().min(1).max(600).nullish(),
    passingScorePercent: z.number().min(0).max(100).nullish(),
    questionsPerPage: z.number().int().min(1).max(50).default(1),
    shuffleQuestions: z.boolean().default(false),
    shuffleOptions: z.boolean().default(false),
    showResultToParticipant: z.boolean().default(false),
    showCorrectAnswers: z.boolean().default(false),
    allowRetake: z.boolean().default(false),
    maxAttempts: z.number().int().min(1).max(10).default(1),
    opensAt: isoDateTimeSchema.nullish(),
    closesAt: isoDateTimeSchema.nullish(),
    /** ไม่ใส่ = ระบบสุ่มให้เอง */
    shortCode: examCodeSchema.optional(),
});
export const examSetUpdateSchema = examSetInputSchema.partial();
export const duplicateExamSetSchema = z.object({
    kind: examKindSchema.default('posttest'),
    title: z.string().trim().min(2).max(300),
});
/**
 * รูปข้อสอบ: รับได้ทั้งไฟล์ที่อัปโหลดเข้าระบบ (path ขึ้นต้นด้วย /api/v1/uploads/)
 * และ URL ภายนอกแบบ https (เผื่อหน่วยงานมีคลังรูปของตัวเองอยู่แล้ว)
 */
export const imageUrlSchema = z
    .string()
    .trim()
    .max(1000)
    .refine((value) => value.startsWith('/api/v1/uploads/') || /^https?:\/\//.test(value), 'ต้องเป็นรูปที่อัปโหลดเข้าระบบ หรือ URL ที่ขึ้นต้นด้วย http(s)');
export const questionOptionInputSchema = z.object({
    label: z.string().trim().min(1).max(1000),
    isCorrect: z.boolean().default(false),
});
export const questionInputSchema = z
    .object({
    type: questionTypeSchema,
    topic: z.string().trim().max(200).nullish(),
    pairKey: z.string().trim().max(100).nullish(),
    stem: z.string().trim().min(1).max(5000),
    imageUrl: imageUrlSchema.nullish(),
    points: z.number().min(0.01).max(100).default(1),
    partialCredit: z.boolean().default(false),
    explanation: z.string().max(5000).nullish(),
    options: z.array(questionOptionInputSchema).min(1).max(10),
})
    .superRefine((question, ctx) => {
    const correctCount = question.options.filter((option) => option.isCorrect).length;
    if (correctCount === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options'],
            message: 'ต้องกำหนดคำตอบที่ถูกต้องอย่างน้อย 1 ตัวเลือก',
        });
    }
    if (question.type !== 'multiple' && question.type !== 'short_text' && correctCount > 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options'],
            message: 'คำถามชนิดนี้ต้องมีคำตอบที่ถูกต้องเพียงตัวเลือกเดียว',
        });
    }
    if (question.type === 'true_false' && question.options.length !== 2) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options'],
            message: 'คำถามถูก/ผิด ต้องมี 2 ตัวเลือกเท่านั้น',
        });
    }
});
export const reorderQuestionsSchema = z.object({
    orderedIds: z.array(uuidSchema).min(1),
});
export const reopenAttemptSchema = z.object({
    extraMinutes: z.number().int().min(1).max(240).default(15),
});
export const participantStatusQuerySchema = z.object({
    status: completionStatusSchema.optional(),
    search: z.string().trim().max(200).optional(),
});
/**
 * นำเข้ารายชื่อล่วงหน้า — แอดมินวางตารางจาก Excel ลงกล่องข้อความ
 * แยกคอลัมน์ด้วย tab (การวางจาก Excel ได้ tab เสมอ) รองรับ comma/semicolon เผื่อวาง CSV
 */
export const invitePasteBodySchema = z.object({
    text: z.string().min(1).max(500_000),
});
export const inviteRowInputSchema = z.object({
    fullName: fullNameSchema,
    email: emailSchema,
    organization: z.string().trim().max(200).nullish(),
    phone: z.string().trim().max(30).nullish(),
});
export const inviteCommitBodySchema = z.object({
    rows: z.array(inviteRowInputSchema).min(1).max(INVITE_IMPORT_MAX_ROWS),
});
/** แถวที่แยกได้จากข้อความที่วางมา — ok=false คือแถวที่ต้องแก้ก่อน */
export const invitePreviewRowSchema = z.object({
    lineNumber: z.number().int(),
    ok: z.boolean(),
    fullName: z.string(),
    email: z.string(),
    organization: z.string().nullable(),
    phone: z.string().nullable(),
    /** มีอยู่ในรายชื่อของหลักสูตรนี้แล้ว → นำเข้าอีกครั้งจะเป็นการอัปเดตข้อมูล ไม่ใช่เพิ่มใหม่ */
    isExisting: z.boolean(),
    problems: z.array(z.string()),
});
export const invitePreviewSchema = z.object({
    rows: z.array(invitePreviewRowSchema),
    totalRows: z.number().int(),
    validRows: z.number().int(),
    invalidRows: z.number().int(),
    existingRows: z.number().int(),
    /** อีเมลซ้ำกันเองในข้อความที่วางมา — เก็บเฉพาะแถวหลังสุด */
    duplicateRows: z.number().int(),
});
export const inviteSchema = z.object({
    id: uuidSchema,
    courseId: uuidSchema,
    fullName: z.string(),
    email: z.string(),
    organization: z.string().nullable(),
    phone: z.string().nullable(),
    token: z.string(),
    /** เคยกดเริ่มทำข้อสอบในหลักสูตรนี้แล้วหรือยัง (ดูจากอีเมลที่ตรงกัน) */
    hasRegistered: z.boolean(),
    createdAt: isoDateTimeSchema,
});
export const inviteImportResultSchema = z.object({
    created: z.number().int(),
    updated: z.number().int(),
    invites: z.array(inviteSchema),
});
export const idParamSchema = z.object({ id: uuidSchema });
export const healthSchema = z.object({
    status: z.enum(['ok', 'degraded']),
    version: z.string(),
    serverTime: isoDateTimeSchema,
    database: z.object({
        connected: z.boolean(),
        latencyMs: z.number().nullable(),
    }),
});
// ─── API envelope ─────────────────────────────────────────────────────────
export const apiErrorSchema = z.object({
    ok: z.literal(false),
    error: z.object({
        code: errorCodeSchema,
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
export function apiSuccessSchema(data) {
    return z.object({ ok: z.literal(true), data });
}
// ─── Analytics (dashboard) ────────────────────────────────────────────────
/**
 * ทุกตัวเลขที่รายงานต้องมี n กำกับเสมอ — ดู docs/06-dashboard.md
 * ค่าเฉลี่ยที่นำมาเทียบกันคำนวณจาก "คนที่ทำครบทั้งคู่" เท่านั้น
 */
export const countWithPercentSchema = z.object({
    n: z.number().int(),
    percent: z.number(),
});
export const meanStatSchema = z.object({
    mean: z.number().nullable(),
    sd: z.number().nullable(),
    n: z.number().int(),
});
export const gainLevelSchema = z.enum(['high', 'medium', 'low']);
export const analyticsSummarySchema = z.object({
    courseId: uuidSchema,
    courseCode: z.string(),
    courseTitle: z.string(),
    generatedAt: isoDateTimeSchema,
    passingScorePercent: z.number().nullable(),
    registered: z.number().int(),
    preDone: countWithPercentSchema,
    postDone: countWithPercentSchema,
    bothDone: countWithPercentSchema,
    prePercent: meanStatSchema,
    postPercent: meanStatSchema,
    gainPercent: meanStatSchema,
    normalizedGain: z.object({
        mean: z.number().nullable(),
        n: z.number().int(),
        level: gainLevelSchema.nullable(),
    }),
    /** null เมื่อชุด posttest ยังไม่ได้ตั้งเกณฑ์ผ่าน */
    passed: countWithPercentSchema.nullable(),
    lowSample: z.boolean(),
});
export const scoreBinSchema = z.object({
    from: z.number(),
    to: z.number(),
    label: z.string(),
    pre: z.number().int(),
    post: z.number().int(),
});
export const durationStatSchema = z.object({
    n: z.number().int(),
    medianSeconds: z.number().nullable(),
    minSeconds: z.number().nullable(),
    maxSeconds: z.number().nullable(),
    q1Seconds: z.number().nullable(),
    q3Seconds: z.number().nullable(),
    /** ต่ำกว่านี้ = ใช้เวลาน้อยผิดปกติ (20% ของมัธยฐาน) */
    suspiciousBelowSeconds: z.number().nullable(),
    values: z.array(z.number()),
});
export const analyticsDistributionSchema = z.object({
    n: z.number().int(),
    binSize: z.number(),
    bins: z.array(scoreBinSchema),
    durations: z.object({ pre: durationStatSchema, post: durationStatSchema }),
});
export const scatterPointSchema = z.object({
    participantId: uuidSchema,
    fullName: z.string(),
    prePercent: z.number(),
    postPercent: z.number(),
    gainPercent: z.number(),
    normalizedGain: z.number().nullable(),
    preDurationSeconds: z.number().int().nullable(),
    postDurationSeconds: z.number().int().nullable(),
});
export const analyticsScatterSchema = z.object({
    n: z.number().int(),
    improved: z.number().int(),
    declined: z.number().int(),
    unchanged: z.number().int(),
    points: z.array(scatterPointSchema),
});
export const topicStatSchema = z.object({
    /** null = ข้อที่ยังไม่ได้ระบุหัวข้อ (หน้าเว็บเป็นคนแปลข้อความ ไม่ hardcode ที่ API) */
    topic: z.string().nullable(),
    prePercentCorrect: z.number().nullable(),
    postPercentCorrect: z.number().nullable(),
    deltaPercent: z.number().nullable(),
    preN: z.number().int(),
    postN: z.number().int(),
    questionCount: z.number().int(),
});
export const analyticsTopicsSchema = z.object({ topics: z.array(topicStatSchema) });
export const distractorStatSchema = z.object({
    optionId: uuidSchema,
    label: z.string(),
    isCorrect: z.boolean(),
    nSelected: z.number().int(),
    percentSelected: z.number().nullable(),
});
export const itemAnalysisRowSchema = z.object({
    pairKey: z.string(),
    topic: z.string().nullable(),
    preStem: z.string().nullable(),
    postStem: z.string().nullable(),
    prePercentCorrect: z.number().nullable(),
    postPercentCorrect: z.number().nullable(),
    deltaPercent: z.number().nullable(),
    /** ความยาก p = สัดส่วนคนตอบถูกใน posttest (0–1) */
    difficulty: z.number().nullable(),
    /** อำนาจจำแนก r_pb — null เมื่อข้อมูลน้อยเกินไปหรือคำนวณไม่ได้ */
    discrimination: z.number().nullable(),
    preN: z.number().int(),
    postN: z.number().int(),
    postQuestionId: uuidSchema.nullable(),
    /** สัดส่วนการเลือกแต่ละตัวเลือกใน pretest — ตัวเลือกเป็นของข้อ pretest จึงแยกจากของ posttest */
    preDistractors: z.array(distractorStatSchema),
    /** สัดส่วนการเลือกแต่ละตัวเลือกใน posttest — ใช้คำนวณธง confusingDistractor */
    distractors: z.array(distractorStatSchema),
});
export const analyticsItemsSchema = z.object({
    items: z.array(itemAnalysisRowSchema),
    minSampleForDiscrimination: z.number().int(),
});
export const liveAttemptSchema = z.object({
    attemptId: uuidSchema,
    participantId: uuidSchema,
    fullName: z.string(),
    examSetId: uuidSchema,
    examSetTitle: z.string(),
    kind: examKindSchema,
    startedAt: isoDateTimeSchema,
    deadlineAt: isoDateTimeSchema.nullable(),
    remainingSeconds: z.number().nullable(),
    answeredCount: z.number().int(),
    questionCount: z.number().int(),
    lastAnswerAt: isoDateTimeSchema.nullable(),
    secondsSinceLastAnswer: z.number().int().nullable(),
    /** ไม่มีการบันทึกคำตอบนานผิดปกติ — อาจเน็ตหลุด */
    isStale: z.boolean(),
});
export const analyticsLiveSchema = z.object({
    serverTime: isoDateTimeSchema,
    staleThresholdSeconds: z.number().int(),
    inProgress: z.number().int(),
    finished: z.number().int(),
    notStarted: z.number().int(),
    attempts: z.array(liveAttemptSchema),
});
// ─── เปรียบเทียบข้ามรอบอบรม (cohort comparison) ─────────────────────────────
/** ?courseIds=a,b,c — ตัดตัวซ้ำออกก่อนนับ เพื่อไม่ให้เลือกหลักสูตรเดียวกันสองครั้งแล้วผ่านขั้นต่ำ */
export const compareQuerySchema = z.object({
    courseIds: z
        .string()
        .transform((value) => [
        ...new Set(value
            .split(',')
            .map((id) => id.trim())
            .filter((id) => id.length > 0)),
    ])
        .pipe(z
        .array(uuidSchema)
        .min(2, 'กรุณาเลือกอย่างน้อย 2 หลักสูตรเพื่อเปรียบเทียบ')
        .max(MAX_COMPARE_COURSES, `เปรียบเทียบได้ครั้งละไม่เกิน ${MAX_COMPARE_COURSES} หลักสูตร`)),
});
export const cohortSummarySchema = analyticsSummarySchema.extend({
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    /**
     * หลักสูตรที่ใช้ข้อสอบ (pre + post) ชุดเดียวกันได้เลขเดียวกัน เริ่มที่ 1 ตามลำดับที่แสดง
     * ใช้บอกแอดมินว่าคะแนนดิบของแถวไหนเทียบกันตรงๆ ได้
     */
    examGroup: z.number().int(),
});
export const cohortTopicCellSchema = z.object({
    courseId: uuidSchema,
    prePercentCorrect: z.number().nullable(),
    postPercentCorrect: z.number().nullable(),
    deltaPercent: z.number().nullable(),
    preN: z.number().int(),
    postN: z.number().int(),
    questionCount: z.number().int(),
});
export const cohortTopicRowSchema = z.object({
    /** null = ข้อที่ยังไม่ได้ระบุหัวข้อ */
    topic: z.string().nullable(),
    /** เรียงตรงกับ cohorts — null เมื่อหลักสูตรนั้นไม่มีหัวข้อนี้ */
    cells: z.array(cohortTopicCellSchema.nullable()),
});
export const cohortComparisonSchema = z.object({
    generatedAt: isoDateTimeSchema,
    /** ทุกหลักสูตรใช้ข้อสอบชุดเดียวกัน — false = ต้องขึ้นคำเตือนว่าคะแนนดิบมาจากข้อสอบต่างชุด */
    sameExam: z.boolean(),
    /** เรียงตามวันเริ่มอบรม (ไม่ระบุวันไว้ท้ายสุด) */
    cohorts: z.array(cohortSummarySchema),
    topics: z.array(cohortTopicRowSchema),
});
export const distributionQuerySchema = z.object({
    bins: z.coerce.number().int().min(2).max(20).default(10),
});
export const exportQuerySchema = z.object({
    format: z.enum(['csv', 'xlsx']).default('csv'),
});
//# sourceMappingURL=schemas.js.map